/**
 * Input validation utilities for dynamic processor inputs.
 * Reusable across all processors that support dynamic inputs.
 */

// Reserved names that cannot be used as custom input names
export const RESERVED_INPUT_NAMES = [
  'config',
  'error',
  'output',
  'result',
  'metadata',
  'status',
  'success',
  'data',
  'step_id',
  'workflow_id',
  'node_id',
  'processor_name',
];

export const MAX_INPUT_NAME_LENGTH = 32;

/**
 * Sanitize input name to valid format:
 * - Lowercase only
 * - Alphanumeric + underscores
 * - No spaces
 */
export function sanitizeInputName(name: string): string {
  return name.toLowerCase().replace(/[^a-z0-9_]/g, '');
}

/**
 * Validate a custom input name
 * 
 * @param name - The proposed input name
 * @param existingNames - Array of already-used input names
 * @returns Validation result with error message if invalid
 */
export function validateInputName(
  name: string,
  existingNames: string[]
): { valid: boolean; error?: string } {
  const sanitized = sanitizeInputName(name);

  // Check if sanitization removed everything
  if (!sanitized) {
    return {
      valid: false,
      error: 'Name must contain at least one alphanumeric character',
    };
  }

  // Check length
  if (sanitized.length > MAX_INPUT_NAME_LENGTH) {
    return {
      valid: false,
      error: `Name must be ${MAX_INPUT_NAME_LENGTH} characters or less`,
    };
  }

  // Check reserved names
  if (RESERVED_INPUT_NAMES.includes(sanitized)) {
    return {
      valid: false,
      error: 'This is a reserved name and cannot be used',
    };
  }

  // Check duplicates
  if (existingNames.includes(sanitized)) {
    return {
      valid: false,
      error: 'An input with this name already exists',
    };
  }

  // Check doesn't start with number
  if (/^\d/.test(sanitized)) {
    return {
      valid: false,
      error: 'Name cannot start with a number',
    };
  }

  return { valid: true };
}

